import { supabase } from "../../services/supabaseClient";
import {
  formatBrazilianPhoneNumber,
  normalizeBrazilianPhoneNumber,
} from "./authPhoneFormat";
import { getUserProfileFields } from "./authSessionUtils";
import { APP_ROUTES, buildAppUrl } from "../navigation/appRoutes";
import { assertStrongPassword } from "./passwordStrength";

export const syncUserProfile = async (nextUser) => {
  if (!nextUser?.id) return null;

  const profile = getUserProfileFields(nextUser);
  const { error } = await upsertExplicitProfile({
    userId: nextUser.id,
    email: nextUser.email || "",
    firstName: profile.firstName,
    lastName: profile.lastName,
    phone: profile.phone,
  });

  return error;
};

export const resetPasswordForEmail = (email) =>
  supabase.auth.resetPasswordForEmail(email, {
    redirectTo: buildAppUrl(APP_ROUTES.LOGIN),
  });

export const updateRecoveryPassword = async (password) => {
  assertStrongPassword(password);
  return supabase.auth.updateUser({ password });
};

export const signUpWithProfile = ({
  email,
  password,
  firstName,
  lastName,
  phone = "",
}) => {
  assertStrongPassword(password);

  const phoneDigits = normalizeBrazilianPhoneNumber(phone);
  const profileData = {
    first_name: firstName,
    last_name: lastName,
  };

  if (phoneDigits) {
    profileData.phone = formatBrazilianPhoneNumber(phoneDigits);
    profileData.phone_digits = phoneDigits;
  }

  return supabase.auth.signUp({
    email,
    password,
    options: {
      data: profileData,
    },
  });
};

export const signInWithPassword = ({ email, password }) =>
  supabase.auth.signInWithPassword({ email, password });

export const startGoogleSignIn = () =>
  supabase.auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo: buildAppUrl(APP_ROUTES.HOME),
    },
  });

export const upsertExplicitProfile = ({
  userId,
  email,
  firstName,
  lastName,
  phone,
}) => {
  const profileRow = {
    user_id: userId,
    email,
    first_name: firstName,
    last_name: lastName,
  };

  if (phone !== undefined) {
    profileRow.phone = formatBrazilianPhoneNumber(phone);
  }

  return supabase.from("profiles").upsert(profileRow, { onConflict: "user_id" });
};

export const updateAccount = async ({
  user,
  userId,
  firstName,
  lastName,
  currentPassword,
  password,
  passwordConfirm,
}) => {
  const nextFirstName = String(firstName || "").trim();
  const nextLastName = String(lastName || "").trim();
  const currentAccountPassword = String(currentPassword || "");
  const nextPassword = String(password || "");
  const nextPasswordConfirm = String(passwordConfirm || "");

  if (!userId) {
    throw new Error("Sessao expirada. Entre novamente antes de editar a conta.");
  }

  if (!nextFirstName || !nextLastName) {
    throw new Error("Informe nome e sobrenome.");
  }

  if (nextPassword || nextPasswordConfirm) {
    if (!currentAccountPassword) {
      throw new Error("Informe a senha atual antes de alterar a senha.");
    }

    assertStrongPassword(nextPassword);

    if (nextPassword !== nextPasswordConfirm) {
      throw new Error("As senhas nao conferem.");
    }

    const { error: passwordCheckError } = await signInWithPassword({
      email: user.email,
      password: currentAccountPassword,
    });

    if (passwordCheckError) {
      throw new Error("Senha atual incorreta.");
    }
  }

  const payload = {
    data: {
      first_name: nextFirstName,
      last_name: nextLastName,
    },
  };

  if (nextPassword) {
    payload.password = nextPassword;
  }

  const { data, error } = await supabase.auth.updateUser(payload);
  if (error) throw error;

  const { error: profileError } = await upsertExplicitProfile({
    userId,
    email: user.email,
    firstName: nextFirstName,
    lastName: nextLastName,
  });
  if (profileError) throw profileError;

  const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;

  return { updatedUser: data.user, session: sessionData.session };
};

export const sendPasswordSetupEmail = (user) => {
  if (!user?.email) {
    throw new Error("Nao foi possivel identificar o email da conta.");
  }

  return resetPasswordForEmail(user.email);
};
